import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, ScrollText, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractGate({ children }) {
  const { user, authHeaders } = useAuth();
  const { lang } = useLanguage();
  const [checking, setChecking] = useState(true);
  const [hasContract, setHasContract] = useState(false);
  const [contractText, setContractText] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    if (!user || user.role === 'admin' || user.role === 'superadmin' || user.role === 'instructor') {
      setHasContract(true);
      setChecking(false);
      return;
    }
    axios.get(`${API}/contract/check`, { headers: authHeaders() })
      .then(res => {
        // Backend tek dogruluk kaynagi: gate_enabled=false (varsayilan) ise
        // sozlesme kapisi hic devreye girmez. Eski backend surumlerinde bu alan
        // response'ta bulunmayabilir (undefined) — geriye donuk uyumluluk icin
        // undefined da KAPALI sayilir (kilitlenmez), boylece yari-deploy
        // sirasinda ogrenci disarida kalmaz.
        const gateEnabled = res.data.gate_enabled === true;
        if (!gateEnabled) {
          setHasContract(true);
          return;
        }
        setHasContract(res.data.has_contract);
        if (!res.data.has_contract) {
          return axios.get(`${API}/contract/generate`, { headers: authHeaders() });
        }
      })
      .then(res => { if (res?.data) setContractText(res.data.contract_text); })
      // fail-open: /contract/check network/500 hatasi verirse ogrenciyi kilitleme
      .catch(() => setHasContract(true))
      .finally(() => setChecking(false));
  }, [user, authHeaders]);

  const handleSign = async () => {
    setSigning(true);
    try {
      await axios.post(`${API}/contract/sign`, { contract_text: contractText }, { headers: authHeaders() });
      setSigned(true);
      setTimeout(() => setHasContract(true), 1500);
    } catch (err) { console.error(err); }
    finally { setSigning(false); }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) setScrolledToBottom(true);
  };

  if (checking) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
      <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (hasContract) return <>{children}</>;

  if (signed) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
      <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="card-clay p-8 text-center max-w-md">
        <CheckCircle size={48} className="mx-auto text-[var(--brand-success)] mb-4" />
        <h2 className="font-display text-2xl font-bold mb-2">{lang === 'tr' ? 'Sözleşme Onaylandı!' : 'Contract Approved!'}</h2>
        <p className="text-sm text-[var(--text-muted)]">{lang === 'tr' ? 'Eğitimlerinize erişebilirsiniz...' : 'Accessing your courses...'}</p>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-app)] flex items-center justify-center p-4" data-testid="contract-gate">
      <div className="max-w-2xl w-full">
        <div className="card-clay p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
              <ScrollText size={24} className="text-sky-600" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold">{lang === 'tr' ? 'Mesafeli Satış Sözleşmesi' : 'Distance Sales Agreement'}</h2>
              <p className="text-xs text-[var(--text-muted)]">{lang === 'tr' ? 'Devam etmek için sözleşmeyi okumanız ve onaylamanız gerekmektedir' : 'Please read and approve the contract to continue'}</p>
            </div>
          </div>

          {/* Contract Text */}
          <div className="bg-gray-50 rounded-xl border-2 border-[var(--border-light)] p-5 max-h-[50vh] overflow-y-auto mb-4 text-sm whitespace-pre-line font-mono leading-relaxed"
            onScroll={handleScroll} data-testid="contract-text">
            {contractText}
          </div>

          {!scrolledToBottom && (
            <div className="text-center mb-3">
              <p className="text-xs text-[var(--text-muted)] flex items-center justify-center gap-1 animate-bounce">
                <ChevronDown size={14} /> {lang === 'tr' ? 'Sözleşmenin tamamını okumak için aşağı kaydırın' : 'Scroll down to read the full contract'}
              </p>
            </div>
          )}

          <button onClick={handleSign} disabled={signing || !scrolledToBottom}
            className="btn-success w-full py-3 text-sm disabled:opacity-40 flex items-center justify-center gap-2" data-testid="sign-contract-btn">
            {signing ? '...' : (
              <><CheckCircle size={16} /> {lang === 'tr' ? 'Sözleşmeyi Okudum ve Onaylıyorum' : 'I Have Read and Approve the Contract'}</>
            )}
          </button>
          {!scrolledToBottom && (
            <p className="text-center text-[10px] text-orange-500 font-bold mt-2">{lang === 'tr' ? 'Onaylamak icin sozlesmenin sonuna kadar okuyun' : 'Read to the end to enable approval'}</p>
          )}
        </div>
      </div>
    </div>
  );
}
